/**
 * ProviderManageInformantsForm
 * ============================
 *
 * Provider-facing modal for managing a patient's informants.  This component
 * is a thin wrapper around InformantManagementContent — it provides only the
 * Radix AlertDialog shell and passes provider-specific configuration.
 *
 * Provider-specific behaviour:
 *   - Uses provider terminology ("Informant" / "Informants")
 *   - Shows the "Resend welcome email" action for informants who haven't
 *     set their own password yet
 *   - Receives patientId and refreshPatientData as explicit props (unlike
 *     the patient version which derives them from context)
 *
 * All informant management logic (data fetching, mutations, screen routing,
 * validation) is handled by InformantManagementContent and the
 * useInformantManagement hook it consumes.
 */

import React from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { XIcon } from '@heroicons/react/outline';
import { InformantManagementContent } from '../../../../components/informantManagement';

const PROVIDER_TERMINOLOGY = {
  singular: 'Informant',
  plural: 'Informants',
  lowerSingular: 'informant',
  lowerPlural: 'informants',
};

// CYP ADHD never calls these people a generic "Informant" — they are the
// child's parent/carer and school representative (HK testing, 2 Sept).
const CYP_TERMINOLOGY = {
  singular: 'Contact',
  plural: 'Parents and school',
  lowerSingular: 'contact',
  lowerPlural: 'parent/carer and school contacts',
};

/**
 * @param {Object}   props
 * @param {boolean}  props.isOpen             – Whether the modal is visible
 * @param {Function} props.onClose            – Called when the modal should close
 * @param {Object}   props.patientDataModel   – Full patient document from Firestore
 * @param {string}   props.patientId          – UID of the patient
 * @param {Function} props.refreshPatientData – Callback to reload patient data after mutations
 * @param {Object}   [props.reportContent] – CYP report documents. Tells the
 *   unlock control whether the first appointment section has been generated.
 */
function ProviderManageInformantsForm({ isOpen, onClose, patientDataModel, patientId, refreshPatientData, reportContent }) {
  const isCypAdhd = patientDataModel?.assessmentModule === 'cypAdhd';
  const dialogTitle = isCypAdhd ? 'Manage parents and school' : 'Manage Informants';
  const terminology = isCypAdhd ? CYP_TERMINOLOGY : PROVIDER_TERMINOLOGY;
  return (
    <AlertDialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-4 md:p-6 rounded-lg shadow-lg max-w-lg w-[calc(100vw-1rem)] max-h-[95vh] overflow-y-auto z-50 transition-all duration-300 ease-in-out">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 z-10"
            aria-label="Close dialog"
          >
            <XIcon className="w-5 h-5" aria-hidden="true" />
          </button>
          
          <AlertDialog.Title className="text-lg font-bold mb-3 pr-6">
            {dialogTitle}
          </AlertDialog.Title>

          <VisuallyHidden.Root>
            <AlertDialog.Description>
              {isCypAdhd
                ? "View and manage the child's parent/carer and school representative, their progress, and assignments"
                : 'View and manage patient informants, their progress, and assignments'}
            </AlertDialog.Description>
          </VisuallyHidden.Root>

          <InformantManagementContent
            patientId={patientId}
            patientDataModel={patientDataModel}
            refreshPatientData={refreshPatientData}
            onClose={onClose}
            callerType="provider"
            terminology={terminology}
            showResendWelcome={true}
            reportContent={reportContent}
          />
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

export default ProviderManageInformantsForm;
